import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, ChevronDown, ChevronRight, Plus, Trash2, Palette, Image as ImageIcon } from 'lucide-react';
import { Card, toast, ConfirmationModal } from '../../components/v2/V2Components';
import PaintImageManager from '../../components/v2/Paint/PaintImageManager';
import PaintService from '../../services/PaintService';
import { useFilePicker } from '../../hooks/useFilePicker';

const SHEEN_OPTIONS = ['Flat', 'Matte', 'Eggshell', 'Satin', 'Semi-Gloss', 'Gloss'];

// Per-row stable ID for React keys. Index-as-key is broken for dynamic lists
// you can add/remove from: deleting row N would make the input previously at
// row N+1 inherit row N's focus/composition state. A monotonic counter gives
// each row an identity that survives mutation. Module-scope so it persists
// across renders without needing a useRef hook in every consumer.
let _colorantRowCounter = 0;
const _nextColorantRowId = () => ++_colorantRowCounter;

// Empty colorant row factory. UI stores rows as ordered arrays of
// {id, label, value}; we serialize to the canonical {label: value} dict on
// save (matches the API/Pydantic shape).
const newColorantRow = () => ({ id: _nextColorantRowId(), label: '', value: '' });

const EMPTY_FORM = {
    color_name: '',
    manufacturer: '',
    sheen: '',
    product_line: '',
    color_code: '',
    product_code: '',
    base: '',
    base_code: '',
    purchase_date: '',
    purchase_store: '',
    order_number: '',
    can_size: '',
    barcode: '',
    notes: '',
    tint_formula_system: '',
    tint_formula_can_size: '',
    tint_formula_rows: [],
    tint_formula_raw_text: '',
};

const tintFormulaToRows = (formula) => {
    if (!formula || !formula.colorants) return [];
    return Object.entries(formula.colorants).map(([label, value]) => ({
        id: _nextColorantRowId(),
        label,
        value,
    }));
};

const rowsToColorants = (rows) => {
    const out = {};
    rows.forEach(({ label, value }) => {
        const trimmedLabel = (label || '').trim();
        if (trimmedLabel) out[trimmedLabel] = value || '';
    });
    return out;
};

/**
 * Build a server-ready payload from form state. Empty strings → null so the API
 * doesn't store empty strings that compare differently from missing data.
 */
const buildPayload = (formData) => {
    const colorants = rowsToColorants(formData.tint_formula_rows);
    const hasFormula =
        Object.keys(colorants).length > 0
        || formData.tint_formula_system.trim()
        || formData.tint_formula_can_size.trim();

    return {
        color_name: formData.color_name.trim() || null,
        manufacturer: formData.manufacturer.trim() || null,
        sheen: formData.sheen || null,
        product_line: formData.product_line.trim() || null,
        color_code: formData.color_code.trim() || null,
        product_code: formData.product_code.trim() || null,
        base: formData.base.trim() || null,
        base_code: formData.base_code.trim() || null,
        purchase_date: formData.purchase_date || null,
        purchase_store: formData.purchase_store.trim() || null,
        order_number: formData.order_number.trim() || null,
        can_size: formData.can_size.trim() || null,
        barcode: formData.barcode.trim() || null,
        notes: formData.notes.trim() || null,
        tint_formula: hasFormula
            ? {
                system: formData.tint_formula_system.trim() || null,
                can_size: formData.tint_formula_can_size.trim() || null,
                colorants,
            }
            : null,
        tint_formula_raw_text: formData.tint_formula_raw_text.trim() || null,
    };
};

/**
 * Hydrate form state from a server Paint object. Inverse of buildPayload.
 */
const paintToFormState = (paint) => ({
    color_name: paint.color_name || '',
    manufacturer: paint.manufacturer || '',
    sheen: paint.sheen || '',
    product_line: paint.product_line || '',
    color_code: paint.color_code || '',
    product_code: paint.product_code || '',
    base: paint.base || '',
    base_code: paint.base_code || '',
    purchase_date: paint.purchase_date ? paint.purchase_date.split('T')[0] : '',
    purchase_store: paint.purchase_store || '',
    order_number: paint.order_number || '',
    can_size: paint.can_size || '',
    barcode: paint.barcode || '',
    notes: paint.notes || '',
    tint_formula_system: paint.tint_formula?.system || '',
    tint_formula_can_size: paint.tint_formula?.can_size || '',
    tint_formula_rows: tintFormulaToRows(paint.tint_formula),
    tint_formula_raw_text: paint.tint_formula_raw_text || '',
});

/**
 * Dedicated create/edit page for a Paint. Replaces the old PaintFormModal.
 *
 * Routes (registered in App.jsx):
 *   /paints/new        → create mode
 *   /paints/:id/edit   → edit mode
 *
 * In edit mode the right column shows PaintImageManager (upload up to 5 photos,
 * mark one primary). In create mode it shows a help panel — image uploads need
 * the paint_id in the R2 path, so the user saves first, then we redirect them
 * to the edit page where the image manager is enabled.
 */
const PaintForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const paintId = isEditMode ? parseInt(id, 10) : null;

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [paint, setPaint] = useState(null);
    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showFormula, setShowFormula] = useState(false);
    const [pendingDiscardDest, setPendingDiscardDest] = useState(null);

    // Snapshot used for dirty-checking. Updated after fetch (edit) so reverting
    // a field back to its loaded value clears the dirty flag.
    const pristineRef = useRef(JSON.stringify(EMPTY_FORM));

    // OS-native file chooser for the Scan label button. Tapping it on iOS gives
    // the "Take Photo / Photo Library / Choose File" sheet; on Android the
    // equivalent system chooser. See hooks/useFilePicker for the rationale on
    // why this matters (the explicit MIME list it used to use broke the iOS
    // sheet and forced a file-browser-only picker).
    const { triggerPicker: openScanPicker, fileInputElement: scanFileInput } = useFilePicker({
        kind: 'image',
        onFiles: (files) => handleScanLabel(files[0]),
        disabled: scanning || saving,
    });

    const isDirty = JSON.stringify(formData) !== pristineRef.current;

    // --- Load existing paint in edit mode ---
    const fetchPaint = useCallback(async () => {
        if (!isEditMode) return;
        setLoading(true);
        try {
            const loaded = await PaintService.getPaint(paintId);
            setPaint(loaded);
            const next = paintToFormState(loaded);
            setFormData(next);
            pristineRef.current = JSON.stringify(next);
            // Auto-expand sections that already have data so the user sees
            // them without hunting for the disclosure toggle.
            setShowFormula(
                (loaded.tint_formula?.colorants && Object.keys(loaded.tint_formula.colorants).length > 0)
                || !!loaded.tint_formula_raw_text
            );
        } catch (err) {
            toast.error(err.message || 'Failed to load paint');
            navigate('/paints');
        } finally {
            setLoading(false);
        }
    }, [isEditMode, paintId, navigate]);

    useEffect(() => {
        fetchPaint();
    }, [fetchPaint]);

    // Browser-level guard against unload while dirty.
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // --- Field handlers ---
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleColorantChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            tint_formula_rows: prev.tint_formula_rows.map(
                (row, i) => i === index ? { ...row, [field]: value } : row
            ),
        }));
    };

    const handleAddColorant = () => {
        setFormData(prev => ({
            ...prev,
            tint_formula_rows: [...prev.tint_formula_rows, newColorantRow()],
        }));
    };

    const handleRemoveColorant = (index) => {
        setFormData(prev => ({
            ...prev,
            tint_formula_rows: prev.tint_formula_rows.filter((_, i) => i !== index),
        }));
    };

    // --- Scan label: prefill empty fields only (never clobber user input) ---
    // Receives a single File; the useFilePicker hook handles the input-value
    // reset so the same file can be re-selected.
    const handleScanLabel = async (file) => {
        if (!file) return;

        setScanning(true);
        try {
            const result = await PaintService.scanPaintLabel(file);
            if (!result.success || !result.paint) {
                toast.error(result.error || 'Scan failed. Try again with a clearer photo.');
                return;
            }
            const scanned = result.paint;
            setFormData(prev => ({
                ...prev,
                color_name: prev.color_name || scanned.color_name || '',
                manufacturer: prev.manufacturer || scanned.manufacturer || '',
                sheen: prev.sheen || scanned.sheen || '',
                product_line: prev.product_line || scanned.product_line || '',
                color_code: prev.color_code || scanned.color_code || '',
                product_code: prev.product_code || scanned.product_code || '',
                base: prev.base || scanned.base || '',
                base_code: prev.base_code || scanned.base_code || '',
                purchase_date: prev.purchase_date || (scanned.purchase_date || '').split('T')[0],
                purchase_store: prev.purchase_store || scanned.purchase_store || '',
                order_number: prev.order_number || scanned.order_number || '',
                can_size: prev.can_size || scanned.can_size || '',
                barcode: prev.barcode || scanned.barcode || '',
                tint_formula_system: prev.tint_formula_system || scanned.tint_formula?.system || '',
                tint_formula_can_size: prev.tint_formula_can_size || scanned.tint_formula?.can_size || '',
                tint_formula_rows: prev.tint_formula_rows.length > 0
                    ? prev.tint_formula_rows
                    : tintFormulaToRows(scanned.tint_formula),
                tint_formula_raw_text: prev.tint_formula_raw_text || scanned.tint_formula_raw_text || '',
            }));
            setShowAdvanced(true);
            if (scanned.tint_formula?.colorants && Object.keys(scanned.tint_formula.colorants).length > 0) {
                setShowFormula(true);
            }
            toast.success('Label scanned. Review the fields before saving.');
        } catch (err) {
            // Branch on HTTP status (see PaintFormModal removal — same toasts).
            let msg;
            if (err.code === 429) {
                msg = 'Monthly scan limit reached.';
            } else if (err.code === 502) {
                msg = err.message || "Couldn't read this label. Try a clearer photo.";
            } else if (err.code === 400) {
                msg = err.message || "This file type isn't supported. Try a JPEG or PNG.";
            } else {
                msg = err.message || 'Scan failed. Please try again.';
            }
            toast.error(msg);
        } finally {
            setScanning(false);
        }
    };

    // --- Save ---
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.color_name.trim() && !formData.color_code.trim()) {
            toast.error('Paint needs at least a color name or a color code.');
            return;
        }

        setSaving(true);
        try {
            const payload = buildPayload(formData);
            if (isEditMode) {
                const updated = await PaintService.updatePaint(paintId, payload);
                setPaint(updated);
                pristineRef.current = JSON.stringify(paintToFormState(updated));
                toast.success('Paint updated');
            } else {
                const created = await PaintService.createPaint(payload);
                toast.success('Paint added to library');
                // Immediately bounce to the edit page so the user can add
                // images. The image manager needs a paint_id and the R2
                // upload path includes it (paints/{user_id}/{paint_id}/...).
                pristineRef.current = JSON.stringify(formData);
                navigate(`/paints/${created.id}/edit`, { replace: true });
                return;
            }
        } catch (err) {
            toast.error(err.message || 'Failed to save paint');
        } finally {
            setSaving(false);
        }
    };

    // --- Cancel / back nav with dirty-check ---
    const handleNavigation = (destination = '/paints') => {
        if (isDirty) {
            setPendingDiscardDest(destination);
        } else {
            navigate(destination);
        }
    };

    const handleConfirmDiscard = () => {
        const dest = pendingDiscardDest;
        setPendingDiscardDest(null);
        // Bypass the dirty check on this navigation by clearing pristine.
        pristineRef.current = JSON.stringify(formData);
        navigate(dest ?? '/paints');
    };

    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page header */}
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-[var(--text-main)] flex items-center gap-2 theme-transition">
                        <Palette size={24} />
                        {isEditMode ? 'Edit Paint' : 'Add Paint'}
                    </h1>
                    <p className="text-sm text-[var(--text-secondary)] theme-transition">
                        {isEditMode
                            ? 'Editing this paint changes it everywhere it\'s used.'
                            : 'Save your paint to the library, then add photos.'}
                    </p>
                </div>
                {/* Scan label button (top-right). Disabled while a scan or save is in flight. */}
                <div>
                    <button
                        type="button"
                        onClick={openScanPicker}
                        disabled={scanning || saving}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-card-hover)] text-[var(--text-main)] rounded-lg border border-[var(--border)] disabled:opacity-50 theme-transition"
                    >
                        <Camera size={16} />
                        {scanning ? 'Scanning...' : 'Scan label'}
                    </button>
                    {scanFileInput}
                </div>
            </div>

            {/* Two-column layout: form (left, span 2) + image manager / help (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-2 p-6">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Identity (always visible) */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Color Name</label>
                                <input
                                    value={formData.color_name}
                                    onChange={(e) => handleChange('color_name', e.target.value)}
                                    placeholder="e.g. Manual Opaline"
                                    className={inputClass}
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Manufacturer</label>
                                <input
                                    value={formData.manufacturer}
                                    onChange={(e) => handleChange('manufacturer', e.target.value)}
                                    placeholder="e.g. Sherwin-Williams"
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Sheen / Finish</label>
                                <select
                                    value={formData.sheen}
                                    onChange={(e) => handleChange('sheen', e.target.value)}
                                    className={inputClass}
                                >
                                    <option value="">Select sheen...</option>
                                    {SHEEN_OPTIONS.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Color Code</label>
                                <input
                                    value={formData.color_code}
                                    onChange={(e) => handleChange('color_code', e.target.value)}
                                    placeholder="e.g. IFC 8112NP"
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        {/* Advanced details (collapsed by default) */}
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(v => !v)}
                            className="flex items-center gap-2 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-main)] theme-transition"
                        >
                            {showAdvanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            Advanced details
                        </button>

                        {showAdvanced && (
                            <div className="space-y-4 pl-6 border-l-2 border-[var(--border)] theme-transition">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Product Line</label>
                                        <input
                                            value={formData.product_line}
                                            onChange={(e) => handleChange('product_line', e.target.value)}
                                            placeholder="e.g. Emerald Interior Flat"
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Product Code (SKU)</label>
                                        <input
                                            value={formData.product_code}
                                            onChange={(e) => handleChange('product_code', e.target.value)}
                                            placeholder="e.g. K35W00351"
                                            className={inputClass}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Base</label>
                                        <input
                                            value={formData.base}
                                            onChange={(e) => handleChange('base', e.target.value)}
                                            placeholder="e.g. Extra White"
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Base Code</label>
                                        <input
                                            value={formData.base_code}
                                            onChange={(e) => handleChange('base_code', e.target.value)}
                                            placeholder="e.g. 650864309"
                                            className={inputClass}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Can Size</label>
                                        <input
                                            value={formData.can_size}
                                            onChange={(e) => handleChange('can_size', e.target.value)}
                                            placeholder="e.g. GALLON"
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Purchase Date</label>
                                        <input
                                            type="date"
                                            value={formData.purchase_date}
                                            onChange={(e) => handleChange('purchase_date', e.target.value)}
                                            className={inputClass}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Purchase Store</label>
                                        <input
                                            value={formData.purchase_store}
                                            onChange={(e) => handleChange('purchase_store', e.target.value)}
                                            placeholder="e.g. SW Store 8035"
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Order Number</label>
                                        <input
                                            value={formData.order_number}
                                            onChange={(e) => handleChange('order_number', e.target.value)}
                                            placeholder="e.g. 0132840"
                                            className={inputClass}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className={labelClass}>Barcode</label>
                                    <input
                                        value={formData.barcode}
                                        onChange={(e) => handleChange('barcode', e.target.value)}
                                        placeholder="e.g. 0132840-001"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Tint formula (collapsed by default) */}
                        <button
                            type="button"
                            onClick={() => setShowFormula(v => !v)}
                            className="flex items-center gap-2 text-sm font-semibold text-[var(--text-secondary)] hover:text-[var(--text-main)] theme-transition"
                        >
                            {showFormula ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            Tint formula
                            {formData.tint_formula_rows.length > 0 && (
                                <span className="text-xs text-[var(--text-muted)]">
                                    ({formData.tint_formula_rows.length} colorant{formData.tint_formula_rows.length === 1 ? '' : 's'})
                                </span>
                            )}
                        </button>

                        {showFormula && (
                            <div className="space-y-3 pl-6 border-l-2 border-[var(--border)] theme-transition">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>System</label>
                                        <input
                                            value={formData.tint_formula_system}
                                            onChange={(e) => handleChange('tint_formula_system', e.target.value)}
                                            placeholder="e.g. CCE, CCA"
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Formula Can Size</label>
                                        <input
                                            value={formData.tint_formula_can_size}
                                            onChange={(e) => handleChange('tint_formula_can_size', e.target.value)}
                                            placeholder="e.g. GALLON"
                                            className={inputClass}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="text-xs font-semibold text-[var(--text-secondary)] uppercase theme-transition">
                                        Colorants
                                    </div>
                                    {formData.tint_formula_rows.length === 0 && (
                                        <p className="text-xs text-[var(--text-muted)] italic">
                                            No colorants yet. Click &quot;Add colorant&quot; or paste raw OCR text below.
                                        </p>
                                    )}
                                    {formData.tint_formula_rows.map((row, i) => (
                                        <div key={row.id} className="flex gap-2">
                                            <input
                                                value={row.label}
                                                onChange={(e) => handleColorantChange(i, 'label', e.target.value)}
                                                placeholder="Code, e.g. B1-Black"
                                                className={`${inputClass} flex-1`}
                                            />
                                            <input
                                                value={row.value}
                                                onChange={(e) => handleColorantChange(i, 'value', e.target.value)}
                                                placeholder="Amount, e.g. 0 OZ, 3/32, 1/128"
                                                className={`${inputClass} flex-1 font-mono text-sm`}
                                            />
                                            <button
                                                type="button"
                                                aria-label={`Remove colorant ${row.label || i + 1}`}
                                                onClick={() => handleRemoveColorant(i)}
                                                className="px-3 text-[var(--text-muted)] hover:text-red-500 theme-transition"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={handleAddColorant}
                                        className="flex items-center gap-2 text-sm text-[var(--primary)] hover:opacity-80"
                                    >
                                        <Plus size={16} />
                                        Add colorant
                                    </button>
                                </div>

                                <div>
                                    <label className={labelClass}>Raw OCR text (fallback)</label>
                                    <textarea
                                        value={formData.tint_formula_raw_text}
                                        onChange={(e) => handleChange('tint_formula_raw_text', e.target.value)}
                                        placeholder="When the colorant table is too noisy to parse, paste the verbatim OCR text here."
                                        rows={3}
                                        className={`${inputClass} font-mono text-sm resize-none`}
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <label className={labelClass}>Notes</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                placeholder="Any additional notes..."
                                rows={2}
                                className={`${inputClass} resize-none`}
                            />
                        </div>

                        {/* Action bar */}
                        <div className="flex space-x-3 pt-4 border-t border-[var(--border)]">
                            <button
                                type="button"
                                onClick={() => handleNavigation('/paints')}
                                className="flex-1 py-3 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving || scanning}
                                className="flex-1 py-3 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                            >
                                {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save Paint')}
                            </button>
                        </div>
                    </form>
                </Card>

                {/* Right column: image gallery in edit mode, help panel in create mode */}
                <Card className="lg:col-span-1 overflow-hidden">
                    {isEditMode && paint ? (
                        <PaintImageManager
                            paintId={paintId}
                            images={paint.images || []}
                            onChange={fetchPaint}
                        />
                    ) : (
                        <div className="p-6 flex flex-col items-center justify-center text-center gap-3 min-h-[300px]">
                            <ImageIcon size={40} className="text-[var(--text-muted)]" />
                            <h3 className="font-semibold text-[var(--text-main)] theme-transition">
                                Photos come next
                            </h3>
                            <p className="text-sm text-[var(--text-secondary)] theme-transition">
                                Save your paint first, then you&apos;ll be able to add up to 5 photos
                                and pick one as the primary swatch.
                            </p>
                        </div>
                    )}
                </Card>
            </div>

            <ConfirmationModal
                isOpen={pendingDiscardDest !== null}
                onClose={() => setPendingDiscardDest(null)}
                onConfirm={handleConfirmDiscard}
                title="Discard unsaved changes?"
                message="Your changes won't be saved if you leave now."
                confirmText="Discard"
                isDanger
            />
        </div>
    );
};

export default PaintForm;
